// ============================================================
//  BEGIN CUSTOM EDIT: Theme Toggle Component | Date: 2025-10-04
// Web Component for auto theme mode with manual light/dark overrides.
// Auto mode follows system preference, click to force opposite, click again to return to auto
// UPDATED: 2025-10-05 | What: Comprehensive fixes - multi-instance sync, debouncing, performance optimizations, cross-tab sync, error handling

/**
 * ThemeToggle Custom Element
 * Defaults to auto mode (follows system preference)
 * Click to manually override to opposite of current display
 * Click again to return to auto mode
 *
 * Storage:
 * - null/undefined = auto mode (follows system)
 * - "light" = force light mode (manual override)
 * - "dark" = force dark mode (manual override)
 */

// ============================================================
//  BEGIN CUSTOM EDIT: Magic String Constants | Date: 2025-10-05
// Extract repeated strings to constants for maintainability
// ============================================================
const THEME_MODES = {
  LIGHT: 'light',
  DARK: 'dark',
  AUTO: 'auto'
};
// ============================================================
//  END CUSTOM EDIT
// ============================================================

class ThemeToggle extends HTMLElement {
  constructor() {
    super();
    this.storageKey = 'theme-mode';

    this.menu = null;
    this.options = [];
    this.placementFrame = null;

    // Bind methods for event listeners
    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handleOptionClick = this.handleOptionClick.bind(this);
    this.handleOptionTouchEnd = this.handleOptionTouchEnd.bind(this);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);
    this.handleWindowResize = this.handleWindowResize.bind(this);
    this.handleSystemChange = this.handleSystemChange.bind(this);

    // ============================================================
    //  BEGIN CUSTOM EDIT: Cross-Tab & Multi-Instance Sync | Date: 2025-10-05
    // Sync theme across tabs and component instances
    // ============================================================
    this.handleStorageChange = this.handleStorageChange.bind(this);
    this.handleThemeChanged = this.handleThemeChanged.bind(this);
    this.handlePageShow = this.handlePageShow.bind(this);
    // ============================================================
    //  BEGIN CUSTOM EDIT: iOS Touch Tap Support | Date: 2025-11-25
    // Track touch interactions separately so Safari/Chrome on iOS can switch modes even when synthetic clicks are suppressed
    // ============================================================
    this.shouldIgnoreClick = false;
    this.touchResetTimer = null;
    this.supportsTouch = 'ontouchend' in window || (navigator && navigator.maxTouchPoints > 0);
    // ============================================================
    //  END CUSTOM EDIT
    // ============================================================
    // ============================================================
    //  END CUSTOM EDIT
    // ============================================================
  }

  connectedCallback() {
    // ============================================================
    //  BEGIN CUSTOM EDIT: Cache DOM Elements | Date: 2025-10-05
    // Cache button reference for performance
    // Find and cache button element to avoid repeated DOM queries
    // ============================================================
    this.button = this.querySelector('[data-theme-toggle-trigger]') || this.querySelector('button');
    this.menu = this.querySelector('[data-theme-toggle-menu]') || null;
    this.options = Array.from(this.querySelectorAll('[data-mode-option]'));
    // ============================================================
    //  END CUSTOM EDIT
    // ============================================================

    if (this.button) {
      // Use bound method (from constructor) instead of arrow function so it can be properly removed
      this.button.addEventListener('click', this.handleTriggerClick);
    }

    if (this.options.length > 0) {
      this.options.forEach((option) => {
        // Use capture phase to fire BEFORE drawer handlers
        option.addEventListener('click', this.handleOptionClick, true);
        if (this.supportsTouch) {
          option.addEventListener('touchend', this.handleOptionTouchEnd, { passive: false, capture: true });
        }
      });
    }

    // ============================================================
    //  BEGIN CUSTOM EDIT: matchMedia Availability Check | Date: 2025-10-05
    // Graceful fallback if matchMedia unavailable
    // Set up system preference watcher with availability check
    // ============================================================
    if (window.matchMedia) {
      this.systemPreferenceQuery = window.matchMedia('(prefers-color-scheme: dark)');

      // Modern browsers support addEventListener on MediaQueryList
      if (this.systemPreferenceQuery.addEventListener) {
        // Use bound method instead of arrow function
        this.systemPreferenceQuery.addEventListener('change', this.handleSystemChange);
      } else {
        // Fallback for older browsers
        this.systemPreferenceQuery.addListener(this.handleSystemChange);
      }
    } else {
      // Fallback: assume light mode if matchMedia unavailable
      console.warn('matchMedia not supported, defaulting to light mode');
      this.systemPreferenceQuery = { matches: false };
    }
    // ============================================================
    //  END CUSTOM EDIT
    // ============================================================

    // ============================================================
    //  BEGIN CUSTOM EDIT: Cross-Tab & Multi-Instance Sync | Date: 2025-10-05
    // Listen for storage changes across tabs and theme changes from other instances
    // Cross-tab synchronization via storage events
    // ============================================================
    window.addEventListener('storage', this.handleStorageChange);

    // Multi-instance synchronization via custom events
    document.addEventListener('theme:changed', this.handleThemeChanged);

    // Browser navigation support (back/forward)
    window.addEventListener('pageshow', this.handlePageShow);

    document.addEventListener('click', this.handleDocumentClick, true);
    this.addEventListener('keydown', this.handleKeydown);
    this.addEventListener('focusout', this.handleFocusOut);
    window.addEventListener('resize', this.handleWindowResize);
    // ============================================================
    //  END CUSTOM EDIT
    // ============================================================

    // Initialize theme - reuses data-initial-mode to avoid redundant localStorage read
    this.init();
  }

  disconnectedCallback() {
    // Clean up event listeners
    if (this.button) {
      this.button.removeEventListener('click', this.handleTriggerClick);
    }

    if (this.options.length > 0) {
      this.options.forEach((option) => {
        option.removeEventListener('click', this.handleOptionClick, true);
        if (this.supportsTouch) {
          option.removeEventListener('touchend', this.handleOptionTouchEnd, true);
        }
      });
    }

    if (this.touchResetTimer) {
      clearTimeout(this.touchResetTimer);
      this.touchResetTimer = null;
    }

    if (this.systemPreferenceQuery && window.matchMedia) {
      if (this.systemPreferenceQuery.removeEventListener) {
        this.systemPreferenceQuery.removeEventListener('change', this.handleSystemChange);
      } else {
        this.systemPreferenceQuery.removeListener(this.handleSystemChange);
      }
    }

    // ============================================================
    //  BEGIN CUSTOM EDIT: Clean Up Global Listeners | Date: 2025-10-05
    // Remove global event listeners to prevent memory leaks
    // ============================================================
    window.removeEventListener('storage', this.handleStorageChange);
    document.removeEventListener('theme:changed', this.handleThemeChanged);
    window.removeEventListener('pageshow', this.handlePageShow);
    document.removeEventListener('click', this.handleDocumentClick, true);
    this.removeEventListener('keydown', this.handleKeydown);
    this.removeEventListener('focusout', this.handleFocusOut);
    window.removeEventListener('resize', this.handleWindowResize);
    if (this.placementFrame) {
      cancelAnimationFrame(this.placementFrame);
      this.placementFrame = null;
    }
    // ============================================================
    //  END CUSTOM EDIT
    // ============================================================
  }

  /**
   * Initialize theme from data-initial-mode or localStorage (fallback)
   * Reuses the value from FOUC prevention script to avoid redundant localStorage reads
   */
  init() {
    // ============================================================
    //  BEGIN CUSTOM EDIT: Prevent Re-initialization | Date: 2025-10-05
    // Skip init if already initialized to prevent theme reverting on mobile drawer open/close
    // ============================================================
    if (this.hasInitialized) {
      return;
    }
    // ============================================================
    //  END CUSTOM EDIT
    // ============================================================

    // Check if FOUC prevention script already set the initial mode
    const initialMode = document.documentElement.getAttribute('data-initial-mode');

    if (initialMode) {
      // Reuse the value from inline script (avoids redundant localStorage read)
      const mode = initialMode === THEME_MODES.AUTO ? null : initialMode;
      this.applyMode(mode, true); // Pass skipEvent flag to prevent duplicate events
    } else {
      // Fallback: read from localStorage if data-initial-mode wasn't set
      const savedMode = this.getSavedMode();
      this.applyMode(savedMode, true); // Pass skipEvent flag to prevent duplicate events
    }

    // ============================================================
    //  BEGIN CUSTOM EDIT: Prevent Re-initialization | Date: 2025-10-05
    // Mark as initialized to prevent re-initialization on drawer open/close
    // ============================================================
    this.hasInitialized = true;
    // ============================================================
    //  END CUSTOM EDIT
    // ============================================================
  }

  /**
   * Get saved theme mode from localStorage
   * @returns {string|null} - 'light', 'dark', or null for auto
   */
  getSavedMode() {
    try {
      const saved = localStorage.getItem(this.storageKey);
      // Only return if it's a valid manual override
      if (saved === THEME_MODES.LIGHT || saved === THEME_MODES.DARK) {
        return saved;
      }
      // Otherwise return null for auto mode
      return null;
    } catch (_e) {
      // ============================================================
      //  BEGIN CUSTOM EDIT: Fix Unused Variable | Date: 2025-10-05
      // Prefix with underscore to indicate intentionally unused
      // localStorage might be unavailable (private browsing)
      // ============================================================
      console.warn('localStorage unavailable, defaulting to auto mode');
      return null;
      // ============================================================
      //  END CUSTOM EDIT
      // ============================================================
    }
  }

  /**
   * Handle trigger button click - toggles popup visibility
   * @param {MouseEvent} event
   */
  handleTriggerClick(event) {
    event.preventDefault();
    event.stopPropagation();
    this.toggleMenu();
  }

  /**
   * Handle option click within popup menu
   * @param {MouseEvent} event
   */
  handleOptionClick(event) {
    // ============================================================
    //  BEGIN CUSTOM EDIT: iOS Touch Tap Support | Date: 2025-11-25
    // Ignore the synthetic click that fires after a handled touch interaction to prevent duplicate toggles
    // ============================================================
    if (event.type === 'click' && this.shouldIgnoreClick) {
      this.shouldIgnoreClick = false;
      if (this.touchResetTimer) {
        clearTimeout(this.touchResetTimer);
        this.touchResetTimer = null;
      }
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    // ============================================================
    //  END CUSTOM EDIT
    // ============================================================

    this.selectModeFromEvent(event);
  }

  /**
   * Ensure touch interactions (iOS Safari/Chrome) trigger selection even if synthetic click is suppressed
   * @param {TouchEvent} event
   */
  // ============================================================
  //  BEGIN CUSTOM EDIT: iOS Touch Tap Support | Date: 2025-11-25
  // ============================================================
  handleOptionTouchEnd(event) {
    this.shouldIgnoreClick = true;
    if (this.touchResetTimer) {
      clearTimeout(this.touchResetTimer);
    }
    this.touchResetTimer = setTimeout(() => {
      this.shouldIgnoreClick = false;
      this.touchResetTimer = null;
    }, 400);

    this.selectModeFromEvent(event);
  }
  // ============================================================
  //  END CUSTOM EDIT
  // ============================================================

  // ============================================================
  //  BEGIN CUSTOM EDIT: iOS Touch Tap Support | Date: 2025-11-25
  // Extracted shared logic so both click and touch handlers stay in sync
  // ============================================================
  selectModeFromEvent(event) {
    if (!event || !event.currentTarget) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const option = event.currentTarget;
    const optionMode = option.getAttribute('data-mode');
    const mode = optionMode === THEME_MODES.AUTO ? null : optionMode;

    // Store click coordinates for circle-reveal transition
    if (event.clientX && event.clientY) {
      this._lastClickX = event.clientX;
      this._lastClickY = event.clientY;
    } else if (event.changedTouches && event.changedTouches[0]) {
      this._lastClickX = event.changedTouches[0].clientX;
      this._lastClickY = event.changedTouches[0].clientY;
    } else {
      // Fallback: use button center
      const rect = this.button ? this.button.getBoundingClientRect() : null;
      this._lastClickX = rect ? rect.left + rect.width / 2 : window.innerWidth / 2;
      this._lastClickY = rect ? rect.top + rect.height / 2 : 0;
    }

    this.setMode(mode);
    this.closeMenu(true);
  }
  // ============================================================
  //  END CUSTOM EDIT
  // ============================================================

  /**
   * Close popup when user clicks outside the component
   * @param {MouseEvent} event
   */
  handleDocumentClick(event) {
    if (!this.isMenuOpen()) {
      return;
    }

    if (!this.contains(event.target)) {
      this.closeMenu();
    }
  }

  /**
   * Close popup when focus moves outside the component
   * @param {FocusEvent} event
   */
  handleFocusOut(event) {
    if (!this.isMenuOpen()) {
      return;
    }

    // Safari returns null for relatedTarget when clicking buttons inside the menu.
    // Delay close so any pending click handlers on menu options fire first.
    if (!event.relatedTarget) {
      setTimeout(() => {
        if (this.isMenuOpen() && !this.contains(document.activeElement)) {
          this.closeMenu();
        }
      }, 100);
      return;
    }

    if (!this.contains(event.relatedTarget)) {
      this.closeMenu();
    }
  }

  /**
   * Keyboard controls for popup interactions
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    const { key } = event;
    const isOpen = this.isMenuOpen();

    if (key === 'Escape' && isOpen) {
      event.preventDefault();
      this.closeMenu(true);
      return;
    }

    if ((key === 'ArrowDown' || key === 'ArrowUp') && !isOpen && document.activeElement === this.button) {
      event.preventDefault();
      this.openMenu();
      return;
    }

    if (!isOpen) {
      return;
    }

    if (key === 'ArrowDown') {
      event.preventDefault();
      this.focusRelativeOption(1);
    } else if (key === 'ArrowUp') {
      event.preventDefault();
      this.focusRelativeOption(-1);
    } else if (key === 'Home') {
      event.preventDefault();
      this.focusOptionByIndex(0);
    } else if (key === 'End') {
      event.preventDefault();
      this.focusOptionByIndex(this.options.length - 1);
    } else if (key === 'Tab') {
      this.closeMenu();
    }
  }

  /**
   * Toggle popup visibility
   * @param {boolean} [force]
   */
  toggleMenu(force) {
    const shouldOpen = typeof force === 'boolean' ? force : !this.isMenuOpen();
    if (shouldOpen) {
      this.openMenu();
    } else {
      this.closeMenu();
    }
  }

  /**
   * Open popup and focus selected option
   */
  openMenu() {
    if (!this.menu) {
      return;
    }

    this.setAttribute('data-open', 'true');

    if (this.button) {
      this.button.setAttribute('aria-expanded', 'true');
    }

    this.focusCurrentOption();
    this.schedulePlacementUpdate();
  }

  /**
   * Close popup
   * @param {boolean} returnFocus - whether focus should return to trigger button
   */
  closeMenu(returnFocus = false) {
    if (!this.menu) {
      return;
    }

    this.removeAttribute('data-open');

    if (this.button) {
      this.button.setAttribute('aria-expanded', 'false');
      if (returnFocus) {
        try {
          this.button.focus({ preventScroll: true });
        } catch (_focusError) {
          this.button.focus();
        }
      }
    }

    this.resetMenuPlacement();
  }

  /**
   * Whether popup is currently visible
   * @returns {boolean}
   */
  isMenuOpen() {
    return this.getAttribute('data-open') === 'true';
  }

  /**
   * Focus the option corresponding to current theme
   */
  focusCurrentOption() {
    if (!this.options.length) {
      return;
    }

    const selected = this.options.find((option) => option.getAttribute('data-selected') === 'true') || this.options[0];
    selected.focus();
  }

  /**
   * Focus option relative to current focus
   * @param {number} delta
   */
  focusRelativeOption(delta) {
    if (!this.options.length) {
      return;
    }

    const activeElement = document.activeElement;
    let currentIndex = this.options.indexOf(activeElement);

    if (currentIndex === -1) {
      currentIndex = 0;
    }

    let nextIndex = currentIndex + delta;

    if (nextIndex < 0) {
      nextIndex = this.options.length - 1;
    } else if (nextIndex >= this.options.length) {
      nextIndex = 0;
    }

    this.focusOptionByIndex(nextIndex);
  }

  /**
   * Focus option by index
   * @param {number} index
   */
  focusOptionByIndex(index) {
    if (!this.options.length) {
      return;
    }

    const clampedIndex = Math.max(0, Math.min(this.options.length - 1, index));
    this.options[clampedIndex].focus();
  }

  /**
   * Reflect the selected theme mode inside the popup
   * @param {string} mode - 'auto', 'light', or 'dark'
   */
  updateMenuSelection(mode) {
    if (!this.options.length) {
      return;
    }

    this.options.forEach((option) => {
      const optionMode = option.getAttribute('data-mode');
      const isSelected = optionMode === mode;
      option.setAttribute('aria-checked', String(isSelected));

      if (isSelected) {
        option.setAttribute('data-selected', 'true');
      } else {
        option.removeAttribute('data-selected');
      }
    });
  }

  /**
   * Ensure popup stays within viewport
   */
  adjustMenuPlacement() {
    if (!this.menu) {
      return;
    }

    this.resetMenuPlacement();

    const margin = 12;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    let rect = this.menu.getBoundingClientRect();
    const triggerRect = this.button ? this.button.getBoundingClientRect() : null;

    const fitsBelow = rect.bottom <= viewportHeight - margin;
    const fitsAbove = triggerRect ? rect.height + margin <= triggerRect.top : false;

    if (!fitsBelow && fitsAbove) {
      this.setAttribute('data-placement', 'top');
      rect = this.menu.getBoundingClientRect();
    }

    let offsetX = 0;
    if (rect.right > viewportWidth - margin) {
      offsetX = (viewportWidth - margin) - rect.right;
    } else if (rect.left < margin) {
      offsetX = margin - rect.left;
    }

    if (offsetX !== 0) {
      this.menu.style.setProperty('--theme-toggle-menu-offset-x', `${offsetX}px`);
    }
  }

  /**
   * Reset inline placement adjustments
   */
  resetMenuPlacement() {
    this.removeAttribute('data-placement');

    if (this.menu) {
      this.menu.style.removeProperty('--theme-toggle-menu-offset-x');
    }
  }

  /**
   * Queue placement adjustment on next frame
   */
  schedulePlacementUpdate() {
    if (!this.menu) {
      return;
    }

    if (this.placementFrame) {
      cancelAnimationFrame(this.placementFrame);
    }

    this.placementFrame = requestAnimationFrame(() => {
      this.placementFrame = null;
      this.adjustMenuPlacement();
    });
  }

  /**
   * Handle viewport resizes while popup is open
   */
  handleWindowResize() {
    if (this.isMenuOpen()) {
      this.schedulePlacementUpdate();
    }
  }

  /**
   * Handle system preference changes - ALWAYS overrides manual mode
   * @param {MediaQueryListEvent} _e - Event object (unused but required by API)
   */
  handleSystemChange(_e) {
    // ============================================================
    //  BEGIN CUSTOM EDIT: Respect Manual Selection | Date: 2025-11-18
    // Only react to system changes while in auto mode
    // If user manually selected a mode (saved mode exists), ignore system changes
    // ============================================================
    if (this.getSavedMode() !== null) {
      return;
    }

    // Also confirm current scheme is auto (covers inline overrides without storage)
    if (this.getCurrentMode() !== THEME_MODES.AUTO) {
      return;
    }

    // Auto mode – reapply to follow the new system preference (no storage change needed)
    // Use screen center for circle-reveal (no click point for system changes)
    this._lastClickX = window.innerWidth / 2;
    this._lastClickY = window.innerHeight / 2;
    this.applyMode(null);
    // ============================================================
    //  END CUSTOM EDIT
    // ============================================================
  }

  // ============================================================
  //  BEGIN CUSTOM EDIT: Cross-Tab Sync Handler | Date: 2025-10-05
  /**
   * Sync theme when changed in another browser tab
   * Handle storage changes from other tabs
   * @param {StorageEvent} e - Storage event
   */
  // ============================================================
  handleStorageChange(e) {
    // Only react to theme-mode changes
    if (e?.key === this.storageKey) {
      const newMode = e.newValue;
      // Apply the new mode (null for auto, or the stored value)
      const mode = newMode === THEME_MODES.LIGHT || newMode === THEME_MODES.DARK ? newMode : null;
      this.applyMode(mode);
    }
  }

  /**
   * Handle theme changed events from other component instances
   * @param {CustomEvent} e - Custom event with theme details
   */
  handleThemeChanged(e) {
    // Don't react to our own events (prevents infinite loops)
    if (e.detail.source === this) {
      return;
    }

    // Update button ARIA label to match new state
    if (this.button && e.detail.mode && e.detail.scheme) {
      const labels = {
        [THEME_MODES.AUTO]: `Auto mode active (currently showing ${e.detail.scheme}). Click to switch to ${e.detail.scheme === THEME_MODES.DARK ? THEME_MODES.LIGHT : THEME_MODES.DARK} mode.`,
        [THEME_MODES.LIGHT]: 'Light mode active. Click to switch to dark mode.',
        [THEME_MODES.DARK]: 'Dark mode active. Click to switch to light mode.'
      };
      this.button.setAttribute('aria-label', labels[e.detail.mode] || 'Toggle theme');
    }

    if (e.detail.mode) {
      this.updateMenuSelection(e.detail.mode);
    }
  }

  /**
   * Handle pageshow event (browser back/forward navigation)
   * @param {PageTransitionEvent} _e - Event object (unused but required by API)
   */
  handlePageShow(_e) {
    // Re-read current mode from DOM in case it changed during navigation
    const currentMode = this.getCurrentMode();
    const savedMode = this.getSavedMode();

    // If there's a mismatch, apply the saved mode
    if (currentMode !== savedMode && currentMode !== THEME_MODES.AUTO && savedMode !== null) {
      this.applyMode(savedMode);
    }
  }
  // ============================================================
  //  END CUSTOM EDIT
  // ============================================================

  /**
   * Set theme mode and save to localStorage
   * @param {string|null} mode - 'light', 'dark', or null for auto
   */
  setMode(mode) {
    // Validate mode - only accept 'light', 'dark', or null
    if (mode !== null && mode !== THEME_MODES.LIGHT && mode !== THEME_MODES.DARK) {
      console.warn(`Invalid theme mode "${mode}". Only '${THEME_MODES.LIGHT}', '${THEME_MODES.DARK}', or null are allowed.`);
      return; // Reject invalid modes
    }

    try {
      if (mode === null) {
        // Auto mode - remove from localStorage
        localStorage.removeItem(this.storageKey);
      } else {
        // Manual override - save to localStorage
        localStorage.setItem(this.storageKey, mode);
      }
    } catch (_e) {
      // ============================================================
      //  BEGIN CUSTOM EDIT: Fix Unused Variable | Date: 2025-10-05
      // Prefix with underscore
      // ============================================================
      console.warn('Failed to save theme preference to localStorage');
      // ============================================================
      //  END CUSTOM EDIT
      // ============================================================
    }

    this.applyMode(mode);
  }

  /**
   * Apply theme mode to DOM
   * @param {string|null} mode - 'light', 'dark', or null for auto
   * @param {boolean} skipEvent - Skip dispatching theme:changed event (for init)
   */
  applyMode(mode, skipEvent = false) {
    let scheme;
    let actualMode;

    if (mode === null) {
      // Auto mode - follow system preference
      actualMode = THEME_MODES.AUTO;
      scheme = this.systemPreferenceQuery.matches ? THEME_MODES.DARK : THEME_MODES.LIGHT;
    } else {
      // Manual override
      actualMode = mode;
      scheme = mode;
    }

    // ============================================================
    //  BEGIN CUSTOM EDIT: View Transitions Circle Reveal | Date: 2026-03-20
    //  What: GPU-accelerated circle-reveal transition via View Transitions API
    // ============================================================
    const currentScheme = document.documentElement.getAttribute('data-display');
    const currentMode = document.documentElement.getAttribute('data-scheme');
    const isInitialLoad = !currentScheme;
    const themeChanging = !isInitialLoad && currentScheme !== scheme;

    // The DOM update function — applies attributes immediately
    const applyAttributes = () => {
      if (currentMode !== actualMode) {
        document.documentElement.setAttribute('data-scheme', actualMode);
      }
      if (currentScheme !== scheme) {
        document.documentElement.setAttribute('data-display', scheme);
      }
    };

    // View Transitions API: circle-reveal on Chrome/Firefox, crossfade on Safari
    const canAnimate = themeChanging
      && !window.matchMedia('(prefers-reduced-motion: reduce)').matches
      && typeof document.startViewTransition === 'function';

    if (canAnimate) {
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

      try {
        const transition = document.startViewTransition(applyAttributes);

        transition.ready.then(() => {
          if (!isSafari) {
            // Chrome/Firefox: circle-reveal from click point
            const x = this._lastClickX ?? window.innerWidth / 2;
            const y = this._lastClickY ?? 0;
            const maxRadius = Math.hypot(
              Math.max(x, window.innerWidth - x),
              Math.max(y, window.innerHeight - y)
            );
            document.documentElement.animate(
              { opacity: [1, 1] },
              { duration: 500, pseudoElement: '::view-transition-old(root)' }
            );
            document.documentElement.animate(
              { opacity: [1, 1], clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${maxRadius}px at ${x}px ${y}px)`] },
              { duration: 500, easing: 'ease-in-out', pseudoElement: '::view-transition-new(root)' }
            );
          } else {
            // Safari: smooth crossfade (screenshots old→new with full page content)
            document.documentElement.animate(
              { opacity: [1, 0] },
              { duration: 400, easing: 'ease-in-out', pseudoElement: '::view-transition-old(root)' }
            );
            document.documentElement.animate(
              { opacity: [0, 1] },
              { duration: 400, easing: 'ease-in-out', pseudoElement: '::view-transition-new(root)' }
            );
          }
        }).catch(() => {});
      } catch (_e) {
        applyAttributes();
      }
    } else {
      // No animation support or reduced motion — apply instantly
      applyAttributes();
    }
    // ============================================================
    //  END CUSTOM EDIT
    // ============================================================

    // Update button ARIA label for accessibility
    if (this.button) {
      const labels = {
        [THEME_MODES.AUTO]: `Auto mode active (currently showing ${scheme}). Click to switch to ${scheme === THEME_MODES.DARK ? THEME_MODES.LIGHT : THEME_MODES.DARK} mode.`,
        [THEME_MODES.LIGHT]: 'Light mode active. Click to switch to dark mode.',
        [THEME_MODES.DARK]: 'Dark mode active. Click to switch to light mode.'
      };

      const newLabel = labels[actualMode] || 'Toggle theme';
      const currentLabel = this.button.getAttribute('aria-label');

      // ============================================================
      //  BEGIN CUSTOM EDIT: Optimize Attribute Updates | Date: 2025-10-05
      // Only update if changed
      // ============================================================
      if (currentLabel !== newLabel) {
        this.button.setAttribute('aria-label', newLabel);
      }
      // ============================================================
      //  END CUSTOM EDIT
      // ============================================================
    }

    this.updateMenuSelection(actualMode);

    // ============================================================
    //  BEGIN CUSTOM EDIT: Add Source to Event | Date: 2025-10-05
    // Include source component to prevent infinite event loops
    // Dispatch custom event for other components to react (unless skipEvent is true)
    // ============================================================
    if (!skipEvent) {
      document.dispatchEvent(new CustomEvent('theme:changed', {
        detail: {
          mode: actualMode,  // 'auto', 'light', or 'dark'
          scheme: scheme,    // Actual scheme being displayed ('light' or 'dark')
          source: this       // Include source to prevent infinite loops
        },
        bubbles: true
      }));
    }
    // ============================================================
    //  END CUSTOM EDIT
    // ============================================================
  }

  /**
   * Get current theme mode from DOM
   * @returns {string} - 'auto', 'light', or 'dark'
   */
  getCurrentMode() {
    return document.documentElement.getAttribute('data-scheme') || THEME_MODES.AUTO;
  }
}

// Register custom element (with check to avoid duplicate registration)
if (!window.customElements.get('theme-toggle')) {
  window.customElements.define('theme-toggle', ThemeToggle);
}

// ============================================================
//  END CUSTOM EDIT
// ============================================================
